import { StreamStatus } from '../../../enums';

export interface ISearchEngineRequest {
  impersonatedUserId?: number;
  params: ISearchParams | ILogSearchParams;
  unRestrictedResponse: boolean;
}

export interface ISearchEngineGameRequest extends ISearchEngineRequest {
  game: ISearchGameParams;
}

export interface ISearchEngineGamesRequest extends ISearchEngineRequest {
  games: ISearchGameParams[];
}

export interface ISearchParams {
  queryString: string;
  size: number;
  page: number;
  dateType?: 'game' | 'created';
  fromDate?: Date | string;
  toDate?: Date | string;
  sortOrder?: 'asc' | 'desc';
  sortField?: string;
  filters?: Array<any>;
  exclusions?: Array<any>;
}

export interface ISearchGameParams {
  teamId: string;
}

export interface ILogSearchParams {
  queryString: string;
  filters?: Array<any>;
  size?: number;
  page?: number;
  fromDate?: Date;
  toDate?: Date;
}

export interface ISearchEngineResponse<TSource> {
  hits: IHits<TSource>;
  aggregations: any;
}

export interface IHits<TSource> {
  total: ITotal,
  max_score: number,
  hits: IDataHit<TSource>[]
}

export interface ITotal {
  relation: String,
  value: number,
}

export interface IDataHit<TSource> {
  '_index': string,
  '_type': string,
  '_id': string,
  '_score': string,
  '_source': TSource
}

export interface IGameSearchResponse extends ISearchEngineResponse<IAsset> {
}

export interface IExchangeSearchResponse extends ISearchEngineResponse<IExchangeAsset> {
}

export interface IAssetAccessSearchResponse extends ISearchEngineResponse<IAssetAccessLog> {
}

export type AssetHit = IDataHit<IAsset>;
export type IExchangeAssetHit = IDataHit<IExchangeAsset>;
export type IAssetAccessLogHit = IDataHit<IAssetAccessLog>;

const KeyAssetTitle = 'title';
const KeyAssetSport = 'sport';
const KeyAssetHomeTeam = 'homeTeam';
const KeyAssetAwayTeam = 'awayTeam';
const KeyAssetVideoType = 'videoType';
const KeyAssetSecurityProfiles = 'securityProfiles';
const KeyAssetTags = 'tags';
const KeyAssetParticipants = 'participants';
const KeyAssetDescription = 'description';
const KeyAssetHomeConference = 'homeConference';
const KeyAssetAwayConference = 'awayConference';
const KeyAssetGameDate = 'gameDate';
const KeyAssetPreviewImageUrl = 'previewImageUrl';
const KeyAssetThumbnailImageUrl = 'thumbnailImageUrl';
const KeyAssetCreatedDate = 'createdDate';
const KeyAssetType = 'type';
const KeyAssetHomeTeamCode = 'homeTeamCode';
const KeyAssetAwayTeamCode = 'awayTeamCode';
const KeyAssetSourceTeamCode = 'sourceTeamCode';
const KeyAssetHomeTeamId = 'homeTeamId';
const KeyAssetAwayTeamId = 'awayTeamId';
const KeyAssetSourceTeamId = 'sourceTeamId';
const KeyAssetGameWeek = 'gameWeek';
const KeyAssetExchangeGameKey = 'exchangeGameKey';
const KeyAssetExchangeQualifier = 'exchangeQualifier';
const KeyAssetExchangeVideoAngle = 'exchangeVideoAngle';
const KeyAssetExchangeComment = 'exchangeComment';
const KeyAssetExchangeScheduleId = 'exchangeScheduleId';
const KeyAssetTeamGameNumber = 'teamGameNumber';
const KeyAssetVisitorTeamGameNumber = 'visitorTeamGameNumber';
const KeyAssetOwner = 'owner';
const KeyAssetStreamURL = 'streamUrl';
const KeyAssetStreamChannel = 'streamChannel';
const KeyAssetStart = 'start';
const KeyAssetStop = 'stop';
const KeyAssetStreamStatus = 'streamStatus';
const KeyAssetChannelId = 'channelId';
const KeyGameId = 'gameId';
const KeyWidth = 'width';
const KeyHeight = 'height';
const KeyGop = 'gop';
const KeyBitrate = 'bitrate';
const KeyStreamBitrate = 'streamBitrate';
const KeyFps = 'fps';
const KeyAmazonRssCompatible = 'amazonRssCompatible';
const KeyRightsHolder = 'rightsHolder';
const KeyBroadcastNetwork = 'broadcastNetwork';
const KeyPhotoRotation = 'photoRotation';
const KeyOrgTagIds = 'orgTagIds';
const KeySha1 = 'sha1';

export const AssetKeys = {
  Title: KeyAssetTitle,
  Sport: KeyAssetSport,
  HomeTeam: KeyAssetHomeTeam,
  AwayTeam: KeyAssetAwayTeam,
  VideoType: KeyAssetVideoType,
  SecurityProfile: KeyAssetSecurityProfiles,
  Tags: KeyAssetTags,
  Participants: KeyAssetParticipants,
  Description: KeyAssetDescription,
  HomeConference: KeyAssetHomeConference,
  AwayConference: KeyAssetAwayConference,
  HomeTeamCode: KeyAssetHomeTeamCode,
  AwayTeamCode: KeyAssetAwayTeamCode,
  GameDate: KeyAssetGameDate,
  ThumbnailImageUrl: KeyAssetThumbnailImageUrl,
  CreatedDate: KeyAssetCreatedDate,
  AssetType: KeyAssetType,
  Owner: KeyAssetOwner,
  Qualifier: KeyAssetExchangeQualifier,
  ExchangeVideoAngle: KeyAssetExchangeVideoAngle,
  GameNumber: KeyAssetTeamGameNumber,
  VisitorGameNumber: KeyAssetVisitorTeamGameNumber,
  StreamURL: KeyAssetStreamURL,
  StreamChannel: KeyAssetStreamChannel,
  Start: KeyAssetStart,
  Stop: KeyAssetStop,
  StreamStatus: KeyAssetStreamStatus,
  Width: KeyWidth,
  Height: KeyHeight,
  Gop: KeyGop,
  BitRate: KeyBitrate,
  Fps: KeyFps,
  AmazonRssCompatible: KeyAmazonRssCompatible,
  RightsHolder: KeyRightsHolder,
  BroadcastNetwork: KeyBroadcastNetwork,
  PhotoRotation: KeyPhotoRotation,
  OrgTagIds: KeyOrgTagIds,
  StreamBitrate: KeyStreamBitrate,
  Sha1: KeySha1,
};

export interface IOrgTagIds {
  name: string,
  orgId: number,
  _id: string,
}

export interface IAsset {
  [KeyAssetAwayConference]: string;
  [KeyAssetAwayTeam]: string;
  [KeyAssetAwayTeamCode]: string;
  [KeyAssetAwayTeamId]: string;
  [KeyAssetGameDate]: string;
  [KeyAssetHomeConference]: string;
  [KeyAssetHomeTeam]: string;
  [KeyAssetHomeTeamCode]: string;
  [KeyAssetHomeTeamId]: string;
  [KeyAssetSport]: string;
  [KeyAssetTitle]: string;
  [KeyAssetVideoType]: string;
  [KeyAssetDescription]: string;
  [KeyAssetParticipants]: string;
  [KeyAssetTags]: string;
  [KeyAssetSecurityProfiles]: string[];
  [KeyAssetPreviewImageUrl]: string;
  [KeyAssetThumbnailImageUrl]: string;
  [KeyAssetCreatedDate]: string;
  [KeyAssetType]: string;
  [KeyAssetOwner]: string;
  [KeyAssetExchangeQualifier]: string;
  [KeyAssetExchangeVideoAngle]: string;
  [KeyAssetTeamGameNumber]: string;
  [KeyAssetVisitorTeamGameNumber]: string;
  [KeyAssetStreamURL]: string;
  [KeyAssetStreamChannel]: string;
  [KeyAssetChannelId]: string;
  [KeyAssetStart]: string;
  [KeyAssetStop]: string;
  [KeyAssetStreamStatus]: StreamStatus;
  // Why the stream is in Error, and when. Set by vault-api, usually mirrored from
  // the stream host. Nullable, not just optional: vault-api clears them by writing
  // null (mongoose strips undefined from updates), so null is what arrives here
  // once a stream has recovered.
  streamErrorReason?: string | null;
  streamErrorAt?: string | null;
  [KeyWidth]: string;
  [KeyHeight]: string;
  [KeyGop]: string;
  [KeyBitrate]: string;
  [KeyFps]: string;
  [KeyAmazonRssCompatible]: boolean;
  [KeyPhotoRotation]: number;
  [KeyOrgTagIds]: IOrgTagIds[];
  // Absent/null = auto (the stream host derives output settings)
  [KeyStreamBitrate]?: number | null;
  streamResolution?: string | null;
  streamFps?: number | null;
  [KeySha1]: string;
  exchangeLiveStreamScheduleId?: string;
}

export interface IExchangeAsset {
  [KeyAssetTitle]: string;
  [KeyAssetSport]: string;
  [KeyAssetHomeTeam]: string;
  [KeyAssetAwayTeam]: string;
  [KeyAssetGameDate]: string;
  [KeyAssetVideoType]: string;
  [KeyAssetSecurityProfiles]: string[];
  [KeyAssetType]: string;
  [KeyAssetPreviewImageUrl]: string;
  [KeyAssetThumbnailImageUrl]: string;
  [KeyAssetCreatedDate]: string;
  [KeyAssetHomeTeamCode]: string;
  [KeyAssetAwayTeamCode]: string;
  [KeyAssetSourceTeamCode]: string;
  [KeyAssetHomeTeamId]: string;
  [KeyAssetAwayTeamId]: string;
  [KeyAssetSourceTeamId]: string;
  [KeyAssetGameWeek]: string;
  [KeyAssetExchangeGameKey]: string;
  [KeyAssetExchangeQualifier]: string;
  [KeyAssetExchangeVideoAngle]: string;
  // VV-226: optional - only present once an owner has left one, and only after the
  // OpenSearch mapping has been resynced for the new field.
  [KeyAssetExchangeComment]?: string;
  [KeyAssetTeamGameNumber]: string;
  [KeyAssetVisitorTeamGameNumber]: string;
  [KeyGameId]: string;
  [KeyAssetExchangeScheduleId]: string;
}

export enum AssetAccessMetadataKey {
  Label = 'label',
  Value = 'value',
}

export enum AssetAccessLogKey {
  UserName = 'userName',
  OrgId = 'orgId',
  OrgName = 'orgName',
  AssetId = 'assetId',
  AssetName = 'assetName',
  ActionDate = 'actionDate',
  ActionType = 'actionType',
  Metadata = 'metadata',
}

export const KeyAssetId = 'assetId';

export interface IAssetAccessMetadata {
  [AssetAccessMetadataKey.Label]: string;
  [AssetAccessMetadataKey.Value]: string;
}

export interface IAssetAccessLog {
  [AssetAccessLogKey.UserName]: string;
  [AssetAccessLogKey.OrgId]: number;
  [AssetAccessLogKey.OrgName]: string;
  [AssetAccessLogKey.AssetId]: string;
  [AssetAccessLogKey.AssetName]: string;
  [AssetAccessLogKey.ActionDate]: string;
  [AssetAccessLogKey.ActionType]: string;
  [AssetAccessLogKey.Metadata]: Array<IAssetAccessMetadata>;
}
